import type { FC } from 'react';
import { Drawer, Collapse } from 'antd';
import { ExclamationCircleFilled, CloseOutlined, CheckCircleFilled } from '@ant-design/icons';
import { colorConstants } from 'config/colorConstants';
import {
    TPO_INFEASIBLE_CONSTRAINT_GROUP,
    TPO_INFEASIBLE_CONSTRAINT_ROW,
    TPO_INFEASIBLE_PLAN_DRAWER,
    tpoPartTestId,
} from '../../../config/testIds';
import type { InfeasibilityConstraint, InfeasibilityConstraintGroup } from '../../../types/infeasiblePlan';
import type { InfeasiblePlanConfig } from '../../../config/infeasiblePlanConfig';

const { Panel } = Collapse;

const DRAWER_WIDTH = 600;
const DRAWER_TOP_OFFSET = 65;

const INFEASIBLE_ICON_STYLE = { color: colorConstants.red, fontSize: 14 };
const FEASIBLE_ICON_STYLE = { color: colorConstants.limeGreen, fontSize: 14 };
const COLLAPSE_STYLE = { background: 'transparent' };
const PANEL_STYLE = { marginBottom: 12, background: '#f5f5f5', borderRadius: 8, border: 'none' };
const BODY_STYLE = { padding: '16px' };
const HEADER_STYLE = { borderBottom: 'none', padding: '16px 24px' };
const CONTENT_WRAPPER_STYLE = { top: DRAWER_TOP_OFFSET, height: `calc(100% - ${DRAWER_TOP_OFFSET}px)` };
const MASK_STYLE = { top: DRAWER_TOP_OFFSET };

const StatusIcon: FC<{ infeasible: boolean }> = ({ infeasible }) =>
    infeasible ? (
        <ExclamationCircleFilled style={INFEASIBLE_ICON_STYLE} />
    ) : (
        <CheckCircleFilled style={FEASIBLE_ICON_STYLE} />
    );

interface ConstraintItemRowProps {
    constraint: InfeasibilityConstraint;
    labels: InfeasiblePlanConfig['labels'];
}

const ConstraintItemRow: FC<ConstraintItemRowProps> = ({ constraint, labels }) => {
    const hasInfeasibility = constraint.infeasible;

    return (
        <div
            className="py-3"
            data-testid={tpoPartTestId(TPO_INFEASIBLE_CONSTRAINT_ROW, constraint.constraint_name)}
            data-state-infeasible={hasInfeasibility ? 'true' : 'false'}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <StatusIcon infeasible={hasInfeasibility} />
                    <span className="font-medium text-sm">{constraint.constraint_name}</span>
                </div>
                <span className={`text-sm ${hasInfeasibility ? 'text-red-500 font-medium' : 'text-gray-600'}`}>
                    {hasInfeasibility ? labels.infeasibleLabel : labels.feasibleLabel}
                </span>
            </div>
        </div>
    );
};

ConstraintItemRow.displayName = 'ConstraintItemRow';

interface PanelHeaderProps {
    title: string;
    hasInfeasibility: boolean;
}

const PanelHeader: FC<PanelHeaderProps> = ({ title, hasInfeasibility }) => (
    <div className="flex items-center gap-2">
        <StatusIcon infeasible={hasInfeasibility} />
        <span className="font-semibold">{title}</span>
    </div>
);

PanelHeader.displayName = 'PanelHeader';

interface InfeasiblePlanDrawerProps {
    open: boolean;
    onClose: () => void;
    relaxedConstraints: InfeasibilityConstraintGroup[];
    config: InfeasiblePlanConfig;
}

const InfeasiblePlanDrawer: FC<InfeasiblePlanDrawerProps> = ({ open, onClose, relaxedConstraints, config }) => {
    const { labels } = config;

    // Groups that blocked the solve are the reason the drawer was opened, so they
    // start expanded. With none flagged, the first group stands in so the drawer
    // never opens fully collapsed.
    const infeasibleGroupKeys = relaxedConstraints
        .filter((group) => group.infeasible)
        .map((group) => group.constraint_group);
    const defaultActiveKeys =
        infeasibleGroupKeys.length > 0
            ? infeasibleGroupKeys
            : relaxedConstraints.slice(0, 1).map((group) => group.constraint_group);

    return (
        <Drawer
            title={
                <div className="flex justify-between items-center w-full">
                    <span className="font-semibold text-base">{labels.drawerTitle}</span>
                    <CloseOutlined
                        onClick={onClose}
                        data-testid={tpoPartTestId(TPO_INFEASIBLE_PLAN_DRAWER, 'close_btn')}
                        className="cursor-pointer text-gray-500 hover:text-gray-700"
                    />
                </div>
            }
            placement="right"
            onClose={onClose}
            open={open}
            width={DRAWER_WIDTH}
            closable={false}
            bodyStyle={BODY_STYLE}
            headerStyle={HEADER_STYLE}
            contentWrapperStyle={CONTENT_WRAPPER_STYLE}
            maskStyle={MASK_STYLE}
        >
            <div data-testid={TPO_INFEASIBLE_PLAN_DRAWER}>
                <Collapse
                    defaultActiveKey={defaultActiveKeys}
                    expandIconPosition="end"
                    bordered={false}
                    style={COLLAPSE_STYLE}
                >
                    {relaxedConstraints.map((group) => (
                        <Panel
                            key={group.constraint_group}
                            header={<PanelHeader title={group.constraint_group} hasInfeasibility={group.infeasible} />}
                            style={PANEL_STYLE}
                        >
                            <div
                                className="rounded-md p-3"
                                data-testid={tpoPartTestId(TPO_INFEASIBLE_CONSTRAINT_GROUP, group.constraint_group)}
                                data-state-infeasible={group.infeasible ? 'true' : 'false'}
                            >
                                {group.constraints.map((constraint) => (
                                    <ConstraintItemRow
                                        key={constraint.constraint_name}
                                        constraint={constraint}
                                        labels={labels}
                                    />
                                ))}
                            </div>
                        </Panel>
                    ))}
                </Collapse>
            </div>
        </Drawer>
    );
};

InfeasiblePlanDrawer.displayName = 'InfeasiblePlanDrawer';
export default InfeasiblePlanDrawer;
